// ─── First Project Tour ───
//
// A focused step-by-step walkthrough that fires the first time a user opens
// their "My First Project" sample. Highlights the split-pane chat (panel +
// input) and the fixed Gantt (toolbar + chart) so brand-new users immediately
// understand the two halves of the workspace.
//
// Auto-launches when:
//   - project.is_sample === true
//   - the per-user storage flag is not set
//   - the existing global UI tour is not currently open
//
// Can also be relaunched on demand via the `mfp:start-tour` window event,
// which the "Reset / Replay" controls in GettingStartedChecklist dispatch.

import { useEffect, useState } from 'react';
import { UITour, type TourStep } from '@/components/UITour';
import { useAuthReady } from '@/hooks/useAuthReady';

const FIRST_PROJECT_TOUR_EVENT = 'mfp:start-tour';
const storageKeyFor = (userId: string) => `mfp_tour_seen_${userId}`;

const FIRST_PROJECT_STEPS: TourStep[] = [
  {
    target: 'chat-panel',
    title: 'Your AI copilot lives here',
    description:
      'The left pane is a persistent chat with TaskBot. Ask it anything about this sample project — "what should I do first?", "explain action items", "show me dependencies". It stays visible while you work in the Gantt on the right.',
    position: 'right',
  },
  {
    target: 'chat-input',
    title: 'Talk in plain English',
    description:
      'Type natural commands or questions. Try things like "add a task for Friday", "link Day 2 to Day 3", or "what is a milestone?". The copilot understands the sample project context.',
    position: 'top',
  },
  {
    target: 'gantt-toolbar',
    title: 'Toolbar — zoom, group, undo',
    description:
      'Switch between Hour / Day / Week, group tasks with View By, undo any change, and reach Archive or Trash. Everything you do here is non-destructive in the sample.',
    position: 'bottom',
  },
  {
    target: 'gantt-chart',
    title: 'The fixed Gantt',
    description:
      'Your sample week lives here. Click a task to open it, double-click to edit, and drag to reschedule. Connector lines show dependencies. Open any task to see action items that teach you a feature step-by-step.',
    position: 'left',
  },
];

interface FirstProjectTourProps {
  /** The currently-open project, or null. */
  project: { id?: string | null; is_sample?: boolean | null } | null | undefined;
}

export function FirstProjectTour({ project }: FirstProjectTourProps) {
  const { user } = useAuthReady();
  const [open, setOpen] = useState(false);

  // Auto-open the very first time a sample project is opened
  useEffect(() => {
    if (!user || !project?.is_sample) return;
    const key = storageKeyFor(user.id);
    if (localStorage.getItem(key) === '1') return;
    // Slight delay so the chat + gantt have mounted and tour targets exist
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user, project?.id, project?.is_sample]);

  // Manual relaunch via window event
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(FIRST_PROJECT_TOUR_EVENT, handler);
    return () => window.removeEventListener(FIRST_PROJECT_TOUR_EVENT, handler);
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (user) localStorage.setItem(storageKeyFor(user.id), '1');
  };

  return (
    <UITour
      isOpen={open}
      onClose={handleClose}
      steps={FIRST_PROJECT_STEPS}
      storageKey={user ? storageKeyFor(user.id) : 'mfp_tour_seen_anon'}
    />
  );
}

/** Trigger the First Project walkthrough from anywhere in the app. */
export function startFirstProjectTour() {
  window.dispatchEvent(new CustomEvent(FIRST_PROJECT_TOUR_EVENT));
}
